// HRPricingPage.jsx - COMPLETE FIXED VERSION WITH USD & REACT ICONS
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./HRPricingPage.css";

// Import React Icons
import {
  MdCheck,
  MdWorkspacePremium,
  MdSecurity,
  MdRefresh,
  MdPerson,
  MdWarning,
  MdAutorenew,
  MdError,
  MdArrowBack,
  MdPrint,
  MdEmail,
  MdAttachMoney,
  MdDashboard,
  MdLogin,
  MdCancel,
  MdCreditCard,
  MdCalendarToday,
  MdReceipt,
  MdHelpOutline,
  MdStar,
  MdTrendingUp,
  MdGroups,
  MdInsights,
  MdIntegrationInstructions,
  MdTranslate,
  MdBrush,
  MdApi,
  MdGroupWork,
  MdHeadsetMic,
  MdSecurityUpdateGood,
  MdBusiness,
  MdCloud,
  MdComputer,
  MdSupportAgent,
  MdVerifiedUser,
  MdLock
} from "react-icons/md";

/* ===================================================
   CONFIG
=================================================== */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "AccWl2Ww5ynRhxF-UFvDYwQFBWnwQgztp58sD7fuBwUJu3bE-l8-YrSpEBQPqJQqNoRKEQkMSF4M2edZ";

/* ===================================================
   PAYPAL SCRIPT LOADER - USD FIXED
=================================================== */
const usePayPalScript = (clientId) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!clientId) {
      setError("PayPal Client ID missing");
      return;
    }

    if (window.paypal) {
      console.log("✅ PayPal already loaded");
      setLoaded(true);
      return;
    }

    console.log("🔄 Loading PayPal SDK with USD...");

    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
    script.async = true;
    script.onload = () => {
      if (window.paypal) {
        console.log("✅ PayPal SDK loaded successfully with USD");
        setLoaded(true);
      } else {
        setError("PayPal SDK loaded but window.paypal is undefined");
      }
    };
    script.onerror = () => {
      setError("Unable to load PayPal SDK. Please refresh the page.");
      console.error("❌ PayPal script loading failed");
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [clientId]);

  return { loaded, error };
};

/* ===================================================
   MAIN COMPONENT
=================================================== */
const HRPricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const paypalRef = useRef(null);
  const [userData, setUserData] = useState({
    email: "",
    userId: "",
    companyId: "",
    name: "",
    token: "",
    isLoggedIn: false
  });
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [paypalError, setPaypalError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");

  const { loaded, error: paypalLoadError } = usePayPalScript(PAYPAL_CLIENT_ID);

  /* ===================================================
     PLAN DATA - UPDATED FOR USD
  =================================================== */
  const plans = {
    pro: {
      name: "Pro Plan",
      monthly: { price: 60, priceText: "$60" },
      yearly: { price: 575, priceText: "$575", originalPrice: "$720", savePercent: 20 },
      features: [
        { icon: <MdGroups />, text: "Up to 100 employees" },
        { icon: <MdInsights />, text: "AI-powered HR automation" },
        { icon: <MdDashboard />, text: "Advanced analytics dashboard" },
        { icon: <MdTrendingUp />, text: "Custom onboarding workflows" },
        { icon: <MdHeadsetMic />, text: "Priority email support" },
        { icon: <MdIntegrationInstructions />, text: "Integration with 5+ HR tools" },
        { icon: <MdTranslate />, text: "Multi-language support" },
        { icon: <MdBrush />, text: "Custom branding" },
        { icon: <MdApi />, text: "API access" },
        { icon: <MdGroupWork />, text: "Team collaboration tools" }
      ],
      highlighted: true,
      description: "Perfect for growing companies",
      icon: <MdWorkspacePremium />
    },
    enterprise: {
      name: "Enterprise Plan",
      monthly: { price: 180, priceText: "$180" },
      yearly: { price: 1725, priceText: "$1,725", originalPrice: "$2,160", savePercent: 20 },
      features: [
        { icon: <MdGroups />, text: "Unlimited employees" },
        { icon: <MdStar />, text: "All Pro features" },
        { icon: <MdPerson />, text: "Dedicated account manager" },
        { icon: <MdInsights />, text: "Custom AI model training" },
        { icon: <MdSecurityUpdateGood />, text: "SLA: 99.9% uptime" },
        { icon: <MdBusiness />, text: "White-label solution" },
        { icon: <MdComputer />, text: "On-premise deployment option" },
        { icon: <MdIntegrationInstructions />, text: "Custom integrations" },
        { icon: <MdSupportAgent />, text: "24/7 phone support" },
        { icon: <MdSecurity />, text: "Advanced security audit" }
      ],
      description: "For large organizations with complex needs",
      icon: <MdVerifiedUser />
    }
  };

  /* ===================================================
     GET USER DATA FROM MULTIPLE SOURCES
  =================================================== */
  useEffect(() => {
    const fetchUserData = async () => {
      console.log("🔄 Fetching user data...");

      const data = {
        email: "",
        userId: "",
        companyId: "",
        name: "",
        token: "",
        isLoggedIn: false
      };

      data.token = sessionStorage.getItem("token") || "";

      if (location.state?.userEmail) {
        data.email = location.state.userEmail;
        console.log("📌 Email from location state:", data.email);
      }

      if (!data.email) {
        data.email = sessionStorage.getItem("userEmail") || "";
        console.log("📌 Email from sessionStorage:", data.email);
      }

      const userDataStr = sessionStorage.getItem("userData");
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData.email && !data.email) data.email = userData.email;
          if (userData._id) data.userId = userData._id;
          if (userData.companyId) data.companyId = userData.companyId;
          if (userData.name) data.name = userData.name;
          console.log("📌 UserData from sessionStorage:", userData);
        } catch (err) {
          console.error("Error parsing userData:", err);
        }
      }

      if (data.token) {
        try {
          const payload = JSON.parse(atob(data.token.split('.')[1]));
          if (payload.email && !data.email) data.email = payload.email;
          if (payload.id && !data.userId) data.userId = payload.id;
          if (payload.companyId && !data.companyId) data.companyId = payload.companyId;
          if (payload.name && !data.name) data.name = payload.name;
          console.log("📌 Data from JWT payload:", payload);
        } catch (e) {
          console.log("Token is not JWT format or cannot decode:", e.message);
        }
      }

      const subscriptionStr = sessionStorage.getItem("hrSubscription");
      if (subscriptionStr) {
        try {
          const subscription = JSON.parse(subscriptionStr);
          if (subscription.userEmail && !data.email) data.email = subscription.userEmail;
          console.log("📌 Data from subscription:", subscription);
        } catch (err) {
          console.error("Error parsing subscription:", err);
        }
      }

      data.isLoggedIn = !!data.email || !!data.token;

      if (data.email) {
        sessionStorage.setItem("userEmail", data.email);
      }

      console.log("✅ Final user data:", data);
      setUserData(data);

      setDebugInfo(`
        Email: ${data.email || 'Not found'}
        UserID: ${data.userId || 'Not found'}
        Token: ${data.token ? 'Present' : 'Missing'}
        Logged In: ${data.isLoggedIn ? 'Yes' : 'No'}
        Source: ${location.state?.fromTrialEnded ? 'Trial Ended' : 'Direct Access'}
      `);
    };

    fetchUserData();

    if (location.state?.fromTrialEnded) {
      setSelectedPlan("pro");
      setBillingCycle("yearly");
    }
  }, [location]);

  /* ===================================================
     CREATE ORDER (BACKEND)
  =================================================== */
  const createOrder = async () => {
    try {
      setPaypalError("");

      console.log("📤 Sending create order request:", {
        plan: selectedPlan,
        billingCycle,
        userEmail: userData.email,
        userId: userData.userId,
        companyId: userData.companyId,
        hasToken: !!userData.token
      });

      const res = await fetch(
        `${API_BASE_URL}/api/payment/create-hr-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: userData.token ? `Bearer ${userData.token}` : "",
          },
          body: JSON.stringify({
            plan: selectedPlan,
            billingCycle,
            userEmail: userData.email,
            userId: userData.userId,
            companyId: userData.companyId
          }),
        }
      );

      const data = await res.json();
      console.log("📥 Create order response:", data);

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}: Failed to create order`);
      }

      if (!data.success) {
        throw new Error(data.error || "Order creation failed");
      }

      return data.orderId;
    } catch (err) {
      console.error("❌ Create order error:", err);
      setPaypalError(err.message);
      throw err;
    }
  };


  /* ===================================================
     CAPTURE ORDER (BACKEND)
  =================================================== */
  const captureOrder = async (orderID) => {
    try {
      console.log("📤 Sending capture order request:", {
        orderID,
        userEmail: userData.email
      });

      const res = await fetch(
        `${API_BASE_URL}/api/payment/capture-hr-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: userData.token ? `Bearer ${userData.token}` : "",
          },
          body: JSON.stringify({
            orderID: orderID,
            userEmail: userData.email
          }),
        }
      );

      const data = await res.json();
      console.log("📥 Capture order response:", data);

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}: Failed to capture order`);
      }

      if (!data.success) {
        throw new Error(data.error || "Payment capture failed");
      }

      const amount = billingCycle === "monthly"
        ? plans[selectedPlan].monthly.price
        : plans[selectedPlan].yearly.price;

      setReceipt({
        invoice: `HR-INV-${Date.now()}`,
        plan: plans[selectedPlan].name,
        billingCycle: billingCycle,
        amount: amount,
        email: userData.email,
        paymentId: orderID,
        captureId: data.captureId,
        date: new Date().toISOString(),
        message: data.message,
        currency: data.subscription?.currency || 'USD'
      });

      const subscriptionData = {
        plan: selectedPlan,
        billingCycle: billingCycle,
        status: 'active',
        isPaid: true,
        paymentMethod: 'paypal',
        lastPaymentDate: new Date().toISOString(),
        endDate: data.subscription?.endDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        currency: 'USD',
        amount: amount
      };

      sessionStorage.setItem("hrSubscription", JSON.stringify(subscriptionData));
      sessionStorage.setItem("userEmail", userData.email);

      window.dispatchEvent(new Event("subscription-updated"));

      setPaymentStatus("success");

    } catch (err) {
      console.error("❌ Capture order error:", err);
      setPaypalError(err.message);
      setPaymentStatus("failed");
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (paymentStatus !== "pending") {
      setProcessing(false);
    }
  }, [paymentStatus]);


  /* ===================================================
     INIT PAYPAL BUTTONS - FIXED FOR USD
  =================================================== */
  useEffect(() => {
    console.log("🔧 PayPal initialization check:", {
      loaded,
      userEmail: userData.email,
      selectedPlan,
      billingCycle,
      paymentStatus,
      hasPayPal: !!window.paypal
    });

    if (!loaded || !window.paypal || !paypalRef.current) {
      console.log("⏳ Waiting for PayPal to load");
      return;
    }

    if (paymentStatus !== "pending") {
      console.log("ℹ️ Payment already finished, skipping PayPal init");
      return;
    }

    console.log("✅ Initializing PayPal buttons");

    paypalRef.current.innerHTML = "";

    try {
      const buttons = window.paypal.Buttons({
        style: {
          layout: "vertical",
          color: "gold",
          shape: "rect",
          label: "paypal",
          height: 48,
          tagline: false,
          fundingicons: true
        },

        createOrder: () => createOrder(),

        onApprove: async (data) => {
          try {
            setProcessing(true);
            await captureOrder(data.orderID);
          } catch (err) {
            setProcessing(false);
            setPaymentStatus("failed");
            setPaypalError(err.message || "Payment capture failed");
          }
        },

        onError: (err) => {
          setProcessing(false);
          setPaymentStatus("failed");
          setPaypalError(err.message || "Payment failed");
        },

        onCancel: () => {
          setProcessing(false);
          setPaymentStatus("cancelled");
        },

        onClick: () => {
          setPaypalError("");
        }
      });

      if (buttons.isEligible()) {
        buttons.render(paypalRef.current);
      } else {
        setPaypalError("PayPal not available in this browser or region.");
      }

    } catch (err) {
      console.error("❌ PayPal init error:", err);
      setPaypalError("Failed to initialize PayPal.");
    }

  }, [loaded, userData.email, selectedPlan, billingCycle, paymentStatus  ]);


  /* ===================================================
     HANDLERS
  =================================================== */
  const handleRetry = () => {
    setPaymentStatus("pending");
    setPaypalError("");
    setProcessing(false);
  };

  const handleGoToLogin = () => {
    navigate("/hr-auth", {
      state: {
        isLoginForm: true,
        returnUrl: '/hr-pricing',
        userEmail: userData.email
      }
    });
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleManualEmail = () => {
    const email = prompt("Please enter your email address:", userData.email || "");
    if (email) {
      const updatedData = { ...userData, email, isLoggedIn: true };
      setUserData(updatedData);
      sessionStorage.setItem("userEmail", email);
      alert(`Email set to: ${email}. You can now proceed with payment.`);
    }
  };

  /* ===================================================
     RENDER
  =================================================== */
  return (
    <div className="hr-pricing-page">
      {/* Back Button */}
      <button
        className="back-button"
        onClick={() => navigate(-1)}
        title="Go back"
      >
        <MdArrowBack size={20} />
        Back
      </button>

      <header className="hr-pricing-header">
        <h1>Choose Your HR Plan</h1>
        <p>Select the perfect plan for your HR needs. All plans include premium features.</p>

        {userData.email && (
          <div className="user-info">
            <MdPerson size={18} className="user-icon" />
            <div className="user-details">
              <span className="user-status">
                {userData.isLoggedIn ? 'Logged in as:' : 'Using email:'}
              </span>
              <strong className="user-email">{userData.email}</strong>
              {userData.name && <span className="user-name"> ({userData.name})</span>}
            </div>
            <button
              onClick={handleManualEmail}
              className="change-email-btn"
            >
              <MdEmail size={16} /> Change
            </button>
          </div>
        )}

        {!userData.email && (
          <div className="user-warning">
            <MdWarning size={20} className="warning-icon" />
            <span className="warning-text">No email found.</span>
            <button onClick={handleManualEmail} className="set-email-btn">
              <MdEmail size={16} /> Set Email
            </button>
            <span className="or-text">or</span>
            <button onClick={handleGoToLogin} className="login-btn-small">
              <MdLogin size={16} /> Login
            </button>
          </div>
        )}
      </header>

      {/* Billing Toggle */}
      <div className="billing-toggle-container">
        <div className="billing-toggle-header">
          <MdCalendarToday size={20} />
          <span className="toggle-label">Billing Period</span>
        </div>

        <div className="billing-toggle-bar">
          <div
            className={`toggle-option ${billingCycle === "monthly" ? "active" : ""}`}
            onClick={() => !processing && setBillingCycle("monthly")}
          >
            <span className="toggle-text">Monthly</span>
            <span className="toggle-price">
              ${plans[selectedPlan].monthly.price}/month
            </span>
          </div>

          <div
            className={`toggle-option ${billingCycle === "yearly" ? "active" : ""}`}
            onClick={() => !processing && setBillingCycle("yearly")}
          >
            <div className="toggle-header">
              <span className="toggle-text">Yearly</span>
              <span className="save-badge">
                <MdAttachMoney size={14} />
                Save 20%
              </span>
            </div>
            <span className="toggle-price">
              ${plans[selectedPlan].yearly.price}/year
            </span>
          </div>

          {/* Toggle indicator */}
          <div
            className="toggle-indicator"
            style={{
              transform: billingCycle === "monthly" ? "translateX(0)" : "translateX(100%)"
            }}
          />
        </div>

        {billingCycle === "yearly" && (
          <div className="yearly-savings-info">
            <MdCheck size={16} />
            <span>Billed annually. You save ${plans[selectedPlan].monthly.price * 12 - plans[selectedPlan].yearly.price} per year!</span>
          </div>
        )}
      </div>

      {/* Plans Section */}
      <div className="plans-container">
        {Object.keys(plans).map((key) => (
          <div
            key={key}
            className={`plan-card ${selectedPlan === key ? "selected" : ""
              } ${plans[key].highlighted ? "highlighted" : ""}`}
            onClick={() => !processing && setSelectedPlan(key)}
          >
            {plans[key].highlighted && (
              <div className="popular-badge">
                <MdStar size={16} /> MOST POPULAR
              </div>
            )}

            <div className="plan-header">
              <div className="plan-icon">{plans[key].icon}</div>
              <h3>{plans[key].name}</h3>
              <p className="plan-description">{plans[key].description}</p>

              <div className="plan-price">
                <span className="price-amount">
                  {billingCycle === "monthly"
                    ? plans[key].monthly.priceText
                    : plans[key].yearly.priceText}
                </span>
                <span className="price-period">
                  per {billingCycle === "monthly" ? "month" : "year"}
                </span>
              </div>

              {billingCycle === "yearly" && plans[key].yearly.savePercent > 0 && (
                <div className="savings">
                  <span className="original-price">
                    {plans[key].yearly.originalPrice}
                  </span>
                  <span className="save-percent">
                    Save {plans[key].yearly.savePercent}%
                  </span>
                </div>
              )}
            </div>

            <div className="plan-features">
              <h4>Features</h4>
              {plans[key].features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="HRP_feature-icon">{feature.icon}</div>
                  <span className="HRP_feature-text">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Payment Section */}
      <div className="payment-section">
        <div className="payment-header">
          <h3><MdSecurity size={24} /> Complete Your Payment</h3>
          <p>Pay securely with PayPal</p>
        </div>

        <div className="order-summary">
          <h4>Order Summary</h4>
          <div className="order-details">
            <div className="order-item">
              <span><MdWorkspacePremium size={18} /> Plan:</span>
              <span>{plans[selectedPlan].name}</span>
            </div>
            <div className="order-item">
              <span><MdCalendarToday size={18} /> Billing:</span>
              <span>{billingCycle === "monthly" ? "Monthly" : "Yearly"}</span>
            </div>
            <div className="order-item">
              <span><MdAttachMoney size={18} /> Amount:</span>
              <span className="order-total">
                {billingCycle === "monthly"
                  ? plans[selectedPlan].monthly.priceText
                  : plans[selectedPlan].yearly.priceText}
              </span>
            </div>
            <div className="order-item">
              <span><MdCreditCard size={18} /> Currency:</span>
              <span>USD (US Dollars)</span>
            </div>
            <div className="order-item">
              <span><MdEmail size={18} /> User Email:</span>
              <span className={`user-email-display ${!userData.email ? 'missing' : ''}`}>
                {userData.email || "Not set"}
              </span>
            </div>
          </div>
        </div>

        <div className="paypal-container">
          {paymentStatus === "pending" && (
            <>
              {!userData.email ? (
                <div className="login-prompt">
                  <div className="warning-icon-large"><MdWarning size={48} /></div>
                  <h4>Email Required</h4>
                  <p>Please set your email address to proceed with payment</p>
                  <div className="action-buttons">
                    <button
                      className="set-email-btn-primary"
                      onClick={handleManualEmail}
                    >
                      <MdEmail size={18} /> Set Email Manually
                    </button>
                    <button
                      className="login-btn-primary"
                      onClick={handleGoToLogin}
                    >
                      <MdLogin size={18} /> Login First
                    </button>
                  </div>
                </div>
              ) : paypalLoadError ? (
                <div className="paypal-error">
                  <div className="error-icon-large"><MdError size={48} /></div>
                  <h4>PayPal Loading Error</h4>
                  <p>{paypalLoadError}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="retry-btn"
                  >
                    <MdRefresh size={18} /> Refresh Page
                  </button>
                </div>
              ) : !loaded ? (
                <div className="paypal-loading">
                  <div className="spinner"></div>
                  <p>Loading PayPal payment system...</p>
                </div>
              ) : (
                <>
                  {paypalError && (
                    <div className="error-message">
                      <span className="error-icon"><MdError size={20} /></span>
                      {paypalError}
                      <button
                        onClick={handleRetry}
                        className="clear-error-btn"
                      >
                        <MdCancel size={16} /> Clear
                      </button>
                    </div>
                  )}

                  <div className="paypal-ready">
                    <div className="paypal-logo">
                      <MdLock size={32} />
                      <span>PayPal Secure Payment</span>
                    </div>
                    <p className="paypal-instruction">
                      Click the PayPal button below to complete your payment
                    </p>
                    <p className="email-note">
                      <MdEmail size={16} /> Payment will be processed in USD for: <strong>{userData.email}</strong>
                    </p>
                  </div>

                  <div ref={paypalRef} className="paypal-button-wrapper">
                    {processing && (
                      <div className="processing-overlay">
                        <div className="spinner"></div>
                        <p>Processing your payment...</p>
                        <p className="processing-note">Do not close this window</p>
                      </div>
                    )}
                  </div>
                  <div className="payment-security">
                    <p className="security-text">
                      <MdSecurity size={18} /> Your payment is securely processed by PayPal.
                    </p>
                    <p className="security-subtext">
                      No credit card information is stored on our servers.
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {paymentStatus === "success" && receipt && (
            <div className="payment-success">
              <div className="success-icon"><MdCheck size={64} /></div>
              <h3>Payment Successful!</h3>
              <p>{receipt.message || "Your HR subscription is now active."}</p>

              <div className="receipt-section">
                <h4><MdReceipt size={24} /> Payment Receipt</h4>
                <div className="receipt-details">
                  <div className="receipt-row">
                    <span><MdReceipt size={18} /> Invoice Number:</span>
                    <strong>{receipt.invoice}</strong>
                  </div>
                  <div className="receipt-row">
                    <span><MdCalendarToday size={18} /> Date:</span>
                    <span>{new Date(receipt.date).toLocaleDateString()}</span>
                  </div>
                  <div className="receipt-row">
                    <span><MdWorkspacePremium size={18} /> Plan:</span>
                    <span>{receipt.plan}</span>
                  </div>
                  <div className="receipt-row">
                    <span><MdCalendarToday size={18} /> Billing Cycle:</span>
                    <span>{receipt.billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}</span>
                  </div>
                  <div className="receipt-row">
                    <span><MdAttachMoney size={18} /> Amount Paid:</span>
                    <strong>${receipt.amount.toLocaleString()} {receipt.currency}</strong>
                  </div>
                  <div className="receipt-row">
                    <span><MdCreditCard size={18} /> Payment ID:</span>
                    <span className="order-id">{receipt.paymentId}</span>
                  </div>
                  <div className="receipt-row">
                    <span><MdEmail size={18} /> Payer Email:</span>
                    <span>{receipt.email}</span>
                  </div>
                </div>
              </div>

              <div className="success-actions">
                <button
                  onClick={handleGoToDashboard}
                  className="dashboard-btn"
                >
                  <MdDashboard size={20} /> Go to HR Dashboard
                </button>
                <button
                  onClick={() => window.print()}
                  className="print-btn"
                >
                  <MdPrint size={20} /> Print Receipt
                </button>
              </div>
            </div>
          )}

          {paymentStatus === "failed" && (
            <div className="payment-failed">
              <div className="failed-icon"><MdError size={64} /></div>
              <h3>Payment Failed</h3>
              <p>{paypalError || 'There was an error processing your payment.'}</p>
              <div className="retry-actions">
                <button
                  onClick={handleRetry}
                  className="retry-btn-primary"
                >
                  <MdRefresh size={20} /> Try Again
                </button>
                <button
                  onClick={handleGoToLogin}
                  className="login-alt-btn"
                >
                  <MdLogin size={20} /> Login & Retry
                </button>
              </div>
            </div>
          )}

          {paymentStatus === "cancelled" && (
            <div className="payment-cancelled">
              <div className="cancelled-icon"><MdWarning size={64} /></div>
              <h3>Payment Cancelled</h3>
              <p>Your payment was cancelled. You can try again anytime.</p>
              <button
                onClick={handleRetry}
                className="retry-btn"
              >
                <MdRefresh size={20} /> Try Again
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Support Section */}
      <div className="support-section">
        <h4><MdHelpOutline size={24} /> Need Help?</h4>
        <div className="support-options">
          <div className="support-option">
            <h5><MdError size={20} /> Payment Issues</h5>
            <p>If PayPal is not working, try:</p>
            <ul>
              <li><MdRefresh size={16} /> Refresh the page</li>
              <li><MdSecurity size={16} /> Clear browser cache</li>
              <li><MdLock size={16} /> Try incognito mode</li>
              <li><MdComputer size={16} /> Use a different browser</li>
              <li><MdAttachMoney size={16} /> Ensure PayPal accepts USD in your country</li>
            </ul>
          </div>
          <div className="support-option">
            <h5><MdEmail size={20} /> Email Issues</h5>
            <p>If email is not recognized:</p>
            <ul>
              <li><MdLogin size={16} /> Login first at /hr-auth</li>
              <li><MdEmail size={16} /> Set email manually using the button above</li>
              <li><MdHeadsetMic size={16} /> Contact support: support@hrcopilot.com</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRPricingPage;